export type AudioDeviceSnapshot = {
  inputs: MediaDeviceInfo[];
  outputs: MediaDeviceInfo[];
  selectedInputId: string;
  selectedOutputId: string;
  inputLabel: string;
  outputLabel: string;
  streamReady: boolean;
  trackMuted: boolean;
  level: number;
  testing: boolean;
  outputSelectionSupported: boolean;
  error: string;
};

type AudioDeviceListener = (snapshot: AudioDeviceSnapshot) => void;

type MediaElementWithSinkId = HTMLMediaElement & {
  setSinkId?: (sinkId: string) => Promise<void>;
};

const INPUT_STORAGE_KEY = 'intez:telephony:audio-input:v14';
const OUTPUT_STORAGE_KEY = 'intez:telephony:audio-output:v14';

const INITIAL_SNAPSHOT: AudioDeviceSnapshot = {
  inputs: [],
  outputs: [],
  selectedInputId: '',
  selectedOutputId: '',
  inputLabel: 'Системный микрофон',
  outputLabel: 'Системное устройство вывода',
  streamReady: false,
  trackMuted: false,
  level: 0,
  testing: false,
  outputSelectionSupported: false,
  error: '',
};

function readStorage(key: string): string {
  if (typeof window === 'undefined') return '';

  try {
    return window.localStorage.getItem(key) || '';
  } catch {
    return '';
  }
}

function writeStorage(key: string, value: string): void {
  if (typeof window === 'undefined') return;

  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    // localStorage может быть недоступен в защищённом режиме.
  }
}

function mediaErrorMessage(error: unknown): string {
  const name = error instanceof DOMException ? error.name : '';

  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Доступ к микрофону запрещён. Разрешите микрофон для портала в настройках браузера.';
  }

  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'Микрофон не найден. Подключите гарнитуру и выберите устройство ещё раз.';
  }

  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'Микрофон занят другим приложением или недоступен операционной системе.';
  }

  if (name === 'OverconstrainedError') {
    return 'Выбранный микрофон больше недоступен. Выберите другое устройство.';
  }

  if (name === 'AbortError') {
    return 'Браузер прервал подключение микрофона. Повторите попытку.';
  }

  return error instanceof Error && error.message
    ? error.message
    : 'Не удалось открыть микрофон.';
}

function displayDeviceLabel(
  device: MediaDeviceInfo | undefined,
  fallback: string,
): string {
  return String(device?.label || fallback).trim() || fallback;
}

export class AudioDeviceManager {
  private static instance: AudioDeviceManager | null = null;

  static getInstance(): AudioDeviceManager {
    AudioDeviceManager.instance ||= new AudioDeviceManager();
    return AudioDeviceManager.instance;
  }

  private listeners = new Set<AudioDeviceListener>();
  private snapshot: AudioDeviceSnapshot = {
    ...INITIAL_SNAPSHOT,
    selectedInputId: readStorage(INPUT_STORAGE_KEY),
    selectedOutputId: readStorage(OUTPUT_STORAGE_KEY),
    outputSelectionSupported: typeof HTMLMediaElement !== 'undefined'
      && 'setSinkId' in HTMLMediaElement.prototype,
  };

  private stream: MediaStream | null = null;
  private audioElement: HTMLAudioElement | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private analyserSource: MediaStreamAudioSourceNode | null = null;
  private levelFrame: number | null = null;
  private deviceChangeBound = false;
  private callInProgress = false;

  private constructor() {
    this.bindDeviceChange();
  }

  subscribe(listener: AudioDeviceListener): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());

    return () => {
      this.listeners.delete(listener);
    };
  }

  getSnapshot(): AudioDeviceSnapshot {
    return {
      ...this.snapshot,
      inputs: [...this.snapshot.inputs],
      outputs: [...this.snapshot.outputs],
    };
  }

  bindOutputElement(element: HTMLAudioElement | null): void {
    this.audioElement = element;

    if (element) {
      element.autoplay = true;
      element.muted = false;
      element.volume = 1;
      element.setAttribute('playsinline', '');
      void this.applyOutputDevice();
    }
  }

  async refreshDevices(requestPermission = false): Promise<AudioDeviceSnapshot> {
    if (!navigator.mediaDevices?.enumerateDevices) {
      const error = 'Браузер не поддерживает получение списка аудиоустройств.';
      this.setSnapshot({ error });
      throw new Error(error);
    }

    let permissionStream: MediaStream | null = null;

    try {
      if (requestPermission && !this.stream) {
        permissionStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter(device => device.kind === 'audioinput');
      const outputs = devices.filter(device => device.kind === 'audiooutput');

      let selectedInputId = this.snapshot.selectedInputId;
      let selectedOutputId = this.snapshot.selectedOutputId;

      if (
        selectedInputId
        && !inputs.some(device => device.deviceId === selectedInputId)
      ) {
        selectedInputId = '';
        writeStorage(INPUT_STORAGE_KEY, '');
      }

      if (
        selectedOutputId
        && !outputs.some(device => device.deviceId === selectedOutputId)
      ) {
        selectedOutputId = '';
        writeStorage(OUTPUT_STORAGE_KEY, '');
      }

      const input = inputs.find(
        device => device.deviceId === selectedInputId,
      );
      const output = outputs.find(
        device => device.deviceId === selectedOutputId,
      );

      this.setSnapshot({
        inputs,
        outputs,
        selectedInputId,
        selectedOutputId,
        inputLabel: displayDeviceLabel(input, 'Системный микрофон'),
        outputLabel: displayDeviceLabel(
          output,
          'Системное устройство вывода',
        ),
        error: '',
      });

      return this.getSnapshot();
    } catch (error) {
      const message = mediaErrorMessage(error);
      this.setSnapshot({ error: message });
      throw new Error(message);
    } finally {
      permissionStream?.getTracks().forEach(track => track.stop());
    }
  }

  async selectInput(deviceId: string): Promise<void> {
    const normalized = String(deviceId || '').trim();

    if (
      normalized
      && !this.snapshot.inputs.some(device => device.deviceId === normalized)
    ) {
      throw new Error('Выбранный микрофон больше недоступен.');
    }

    if (this.callInProgress) {
      throw new Error('Нельзя менять микрофон во время активного звонка.');
    }

    if (normalized === this.snapshot.selectedInputId) return;

    writeStorage(INPUT_STORAGE_KEY, normalized);
    this.releaseStream();

    const selected = this.snapshot.inputs.find(
      device => device.deviceId === normalized,
    );

    this.setSnapshot({
      selectedInputId: normalized,
      inputLabel: displayDeviceLabel(selected, 'Системный микрофон'),
      error: '',
    });
  }

  async selectOutput(deviceId: string): Promise<void> {
    const normalized = String(deviceId || '').trim();

    if (
      normalized
      && !this.snapshot.outputs.some(device => device.deviceId === normalized)
    ) {
      throw new Error('Выбранное устройство вывода больше недоступно.');
    }

    writeStorage(OUTPUT_STORAGE_KEY, normalized);

    const selected = this.snapshot.outputs.find(
      device => device.deviceId === normalized,
    );

    this.setSnapshot({
      selectedOutputId: normalized,
      outputLabel: displayDeviceLabel(
        selected,
        'Системное устройство вывода',
      ),
      error: '',
    });

    await this.applyOutputDevice();
  }

  async startTest(): Promise<MediaStream> {
    const stream = await this.prepareCallStream();
    this.callInProgress = false;
    this.setSnapshot({ testing: true });
    return stream;
  }

  stopTest(): void {
    if (this.callInProgress) return;
    this.releaseStream();
  }

  async prepareCallStream(): Promise<MediaStream> {
    const existing = this.getUsableTrack();

    if (existing && this.stream) {
      existing.enabled = true;
      this.callInProgress = true;
      this.setSnapshot({
        streamReady: true,
        trackMuted: existing.muted,
        testing: false,
        error: existing.muted
          ? 'Микрофон подключён, но браузер пока не получает от него звук.'
          : '',
      });
      return this.stream;
    }

    this.releaseStream();

    if (!navigator.mediaDevices?.getUserMedia) {
      const message = 'Браузер не поддерживает доступ к микрофону.';
      this.setSnapshot({ error: message });
      throw new Error(message);
    }

    const selectedInputId = this.snapshot.selectedInputId;
    const audio: MediaTrackConstraints = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
      ...(selectedInputId
        ? { deviceId: { exact: selectedInputId } }
        : {}),
    };

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio,
        video: false,
      });
      const track = stream.getAudioTracks()[0];

      if (!track || track.readyState !== 'live') {
        stream.getTracks().forEach(item => item.stop());
        throw new Error('Микрофон не создал активную аудиодорожку.');
      }

      track.enabled = true;
      this.stream = stream;
      this.callInProgress = true;

      track.addEventListener('mute', this.handleTrackMute);
      track.addEventListener('unmute', this.handleTrackUnmute);
      track.addEventListener('ended', this.handleTrackEnded);

      await this.refreshDevices(false).catch(() => this.getSnapshot());
      this.startLevelMeter(stream);

      const settings = track.getSettings();
      const currentInput = this.snapshot.inputs.find(
        device => device.deviceId === settings.deviceId,
      );

      this.setSnapshot({
        streamReady: true,
        trackMuted: track.muted,
        testing: false,
        inputLabel: displayDeviceLabel(
          currentInput,
          track.label || this.snapshot.inputLabel,
        ),
        error: track.muted
          ? 'Микрофон открыт, но аудиодорожка временно отключена устройством.'
          : '',
      });

      console.info('[telephony] call microphone stream prepared', {
        label: track.label || 'default microphone',
        deviceId: settings.deviceId || 'default',
        enabled: track.enabled,
        muted: track.muted,
        readyState: track.readyState,
      });

      return stream;
    } catch (error) {
      const message = mediaErrorMessage(error);
      this.setSnapshot({
        streamReady: false,
        trackMuted: false,
        level: 0,
        testing: false,
        error: message,
      });
      throw new Error(message);
    }
  }

  markCallInProgress(active: boolean): void {
    this.callInProgress = active;

    if (!active && !this.snapshot.testing) {
      this.releaseStream();
    }
  }

  releaseCallStream(): void {
    this.callInProgress = false;
    this.releaseStream();
  }

  getActiveStream(): MediaStream | null {
    return this.stream;
  }

  async applyOutputDevice(): Promise<void> {
    const element = this.audioElement as MediaElementWithSinkId | null;

    if (!element?.setSinkId) return;

    try {
      await element.setSinkId(this.snapshot.selectedOutputId || 'default');
      this.setSnapshot({ error: '' });
    } catch (error) {
      const message = error instanceof Error
        ? `Не удалось выбрать устройство вывода: ${error.message}`
        : 'Не удалось выбрать устройство вывода.';
      this.setSnapshot({ error: message });
      throw new Error(message);
    }
  }

  private getUsableTrack(): MediaStreamTrack | null {
    if (!this.stream) return null;

    return this.stream.getAudioTracks().find(
      track => track.readyState === 'live',
    ) || null;
  }

  private releaseStream(): void {
    const track = this.getUsableTrack();

    if (track) {
      track.removeEventListener('mute', this.handleTrackMute);
      track.removeEventListener('unmute', this.handleTrackUnmute);
      track.removeEventListener('ended', this.handleTrackEnded);
    }

    this.stream?.getTracks().forEach(item => item.stop());
    this.stream = null;
    this.stopLevelMeter();

    this.setSnapshot({
      streamReady: false,
      trackMuted: false,
      level: 0,
      testing: false,
    });
  }

  private startLevelMeter(stream: MediaStream): void {
    this.stopLevelMeter();

    try {
      this.audioContext = new AudioContext();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 512;
      this.analyser.smoothingTimeConstant = 0.72;
      this.analyserSource = this.audioContext.createMediaStreamSource(stream);
      this.analyserSource.connect(this.analyser);

      const samples = new Uint8Array(this.analyser.fftSize);

      const measure = () => {
        const analyser = this.analyser;

        if (!analyser || !this.stream) return;

        analyser.getByteTimeDomainData(samples);

        let sum = 0;

        for (const sample of samples) {
          const normalized = (sample - 128) / 128;
          sum += normalized * normalized;
        }

        const rms = Math.sqrt(sum / samples.length);
        const level = Math.min(1, Math.max(0, rms * 7));

        if (Math.abs(level - this.snapshot.level) > 0.01) {
          this.setSnapshot({ level });
        }

        this.levelFrame = window.requestAnimationFrame(measure);
      };

      this.levelFrame = window.requestAnimationFrame(measure);
    } catch (error) {
      console.warn('[telephony] microphone level meter unavailable', error);
    }
  }

  private stopLevelMeter(): void {
    if (this.levelFrame !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(this.levelFrame);
    }

    this.levelFrame = null;

    try {
      this.analyserSource?.disconnect();
    } catch {
      // no-op
    }

    try {
      this.analyser?.disconnect();
    } catch {
      // no-op
    }

    this.analyserSource = null;
    this.analyser = null;

    const context = this.audioContext;
    this.audioContext = null;

    if (context && context.state !== 'closed') {
      void context.close().catch(() => undefined);
    }
  }

  private bindDeviceChange(): void {
    if (
      this.deviceChangeBound
      || typeof navigator === 'undefined'
      || !navigator.mediaDevices
    ) return;

    this.deviceChangeBound = true;

    navigator.mediaDevices.addEventListener('devicechange', () => {
      void this.refreshDevices(false).catch(() => undefined);
    });
  }

  private handleTrackMute = () => {
    this.setSnapshot({
      trackMuted: true,
      error: 'Микрофон подключён, но аудиодорожка отключена устройством.',
    });
  };

  private handleTrackUnmute = () => {
    this.setSnapshot({
      trackMuted: false,
      error: '',
    });
  };

  private handleTrackEnded = () => {
    this.callInProgress = false;
    this.releaseStream();
    this.setSnapshot({
      error: 'Микрофон был отключён. Подключите устройство и повторите звонок.',
    });
  };

  private setSnapshot(patch: Partial<AudioDeviceSnapshot>): void {
    this.snapshot = {
      ...this.snapshot,
      ...patch,
    };

    const snapshot = this.getSnapshot();

    for (const listener of this.listeners) {
      try {
        listener(snapshot);
      } catch (error) {
        console.error('[telephony] audio device listener failed', error);
      }
    }
  }
}

export const audioDeviceManager = AudioDeviceManager.getInstance();
